import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Autocomplete, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useFormikContext } from "formik";
import { useEffect, useMemo } from "react";
import useAPI from "../../../../../../api/hooks/useAPI";
import useColors from "../../../../../../api/hooks/useColors";
import useIsIntegrated from "../../../../../../api/hooks/useIsIntegrated";
import { jiraIssuesKeys } from "../../../../../../api/queryKeys";
import AnchorPopper from "../../../../../../components/forms/AnchorPopper";
import {
  getTextFieldInputProps,
  StyledTextField,
  textFieldLabelProps,
} from "../../../../../../components/forms/styles/formFieldStyles";

const JiraSelectedMisconfigurations = ({ fullControl }) => {
  const colors = useColors();
  const { values, errors, touched, handleBlur, setFieldValue } =
    useFormikContext();
  const { getMisconfigurationsWithTickets } = useAPI();
  const { isIntegrated, isIntegratedLoading } = useIsIntegrated("jira");

  const misconfigurationOptions = fullControl?.misconfigurations;

  const selectedMisconfigurations = useMemo(() => {
    return values.selectedMisconfigurations || [];
  }, [values.selectedMisconfigurations]);

  // Misconfiguration IDs with tickets
  const {
    data: misconfigurationIdsWithTickets,
    isSuccess: misconfigurationIdsWithTicketsIsSuccess,
  } = useQuery({
    queryKey: jiraIssuesKeys.jiraMisconfigurationsWithTickets(
      fullControl?.control_id
    ),
    queryFn: () => getMisconfigurationsWithTickets(fullControl?.control_id),
    select: (response) => response.data,
    enabled: fullControl !== null && isIntegrated && !isIntegratedLoading,
  });

  const hasError =
    !!touched.selectedMisconfigurations && !!errors.selectedMisconfigurations;
  const errorMessage =
    hasError && typeof errors.selectedMisconfigurations === "string"
      ? errors.selectedMisconfigurations
      : "";

  const fieldLabel = useMemo(() => {
    const hasValue = selectedMisconfigurations?.length > 0;
    const labelText = hasValue
      ? "Selected misconfigurations"
      : "Select misconfigurations";

    return <Typography variant="caption">{labelText}</Typography>;
  }, [selectedMisconfigurations]);

  // Filter out misconfigurations with tickets
  const filterIrrelevantMisconfigurations = (misconfigurations) => {
    return misconfigurations.filter(
      (misconfig) =>
        !misconfig.is_excluded &&
        !misconfigurationIdsWithTickets?.includes(misconfig.misconfiguration_id)
    );
  };

  useEffect(() => {
    setFieldValue(
      "selectedMisconfigurations",
      filterIrrelevantMisconfigurations(values.selectedMisconfigurations)
    );
  }, [misconfigurationOptions, misconfigurationIdsWithTicketsIsSuccess]);

  return (
    <>
      {misconfigurationOptions && (
        <Autocomplete
          options={filterIrrelevantMisconfigurations(misconfigurationOptions)}
          value={filterIrrelevantMisconfigurations(
            values.selectedMisconfigurations
          )}
          multiple
          filterSelectedOptions
          getOptionLabel={(option) => option.display_name || ""}
          isOptionEqualToValue={(option, value) =>
            option.misconfiguration_id === value.misconfiguration_id
          }
          onChange={(_, newValue) =>
            setFieldValue("selectedMisconfigurations", newValue)
          }
          popupIcon={<ExpandMoreIcon />}
          PopperComponent={AnchorPopper}
          renderInput={(params) => (
            <StyledTextField
              {...params}
              margin="dense"
              label={fieldLabel}
              fullWidth
              required
              error={hasError}
              helperText={errorMessage}
              onBlur={handleBlur}
              InputProps={getTextFieldInputProps(params.InputProps)}
              InputLabelProps={textFieldLabelProps(params.InputLabelProps)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor:
                    colors.textfield_outlined_enabled_auto_generated_bg,
                },
              }}
            />
          )}
        />
      )}
    </>
  );
};

export default JiraSelectedMisconfigurations;
